/**
 * DeletionController - Handles all deletion operations
 * 
 * Manages:
 * - deleteSelected() - main deletion orchestrator
 * - deleteSelectedDevices() - delete all selected devices
 * - deleteDevice(deviceId) - delete single device
 * - deleteConnection(connectionId) - delete single connection
 * - deleteSelectedCables() - delete cables for selected devices
 * - clearRouting() - alias for deleteSelectedCables
 */

import { deepClone } from '../utils/helpers.js';
import { historyService } from '../services/UndoManager.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import {
    createDeviceRestoreFunctions,
    createConnectionRestoreFunctions,
    createBatchConnectionRestoreFunctions,
    createMultiDeviceRestoreFunctions
} from '../services/UndoManager.js';
import { showToast } from './ToastController.js';

// ============= STATE =============

// Module dependencies (injected via init)
let deps = null;

// ============= INIT =============

/**
 * Initialize the deletion controller with dependencies.
 * 
 * @param {Object} dependencies - Dependencies
 * @param {Object} dependencies.instanceModel - Device instance model
 * @param {Object} dependencies.connectionModel - Connection model
 * @param {Object} dependencies.definitionModel - Template definition model
 * @param {Object} dependencies.selectionState - Selection state { devices: Set, connections: Set }
 * @param {Object} dependencies.state - App state (for selectedConnectionId, selectedTemplateId)
 * @param {Function} dependencies.clearDeviceSelection - Clear device selection
 * @param {Function} dependencies.updateSelectionVisuals - Update selection visuals
 * @param {Function} dependencies.updateSelectionButtons - Update toolbar buttons
 * @param {Function} dependencies.hasDeviceSelection - Check if devices are selected
 * @param {Function} dependencies.scheduleAutosave - Schedule autosave
 * @param {Function} dependencies.deleteSelectedTemplate - Delete selected library template
 */
export function initDeletionController(dependencies) {
    deps = dependencies;
}

// ============= PRIVATE HELPERS =============

/**
 * Record an undoable action
 */
function recordAction(type, description, undoFn, redoFn) {
    historyService.recordAction(type, description, undoFn, redoFn);
}

// ============= PUBLIC API =============

/**
 * Delete whatever is currently selected (device, connection, or template)
 */
export function deleteSelected() {
    if (!deps) {
        console.warn('DeletionController: not initialized');
        return;
    }
    
    const { hasDeviceSelection, state, deleteSelectedTemplate } = deps;
    
    // Handle device selection (single or multi)
    if (hasDeviceSelection()) {
        deleteSelectedDevices();
        return;
    }
    
    if (state.selectedConnectionId) {
        deleteConnection(state.selectedConnectionId);
    } else if (state.selectedTemplateId) {
        // Delete from LIBRARY: delete the template AND all its instances on canvas
        deleteSelectedTemplate();
    }
}

/**
 * Delete all selected devices and their connections
 * @param {boolean} skipHistory - If true, don't record to undo history
 */
export function deleteSelectedDevices(skipHistory = false) {
    if (!deps) {
        console.warn('DeletionController: not initialized');
        return;
    }
    
    const {
        instanceModel,
        connectionModel,
        selectionState,
        clearDeviceSelection,
        updateSelectionButtons,
        scheduleAutosave
    } = deps;
    
    const deviceIds = Array.from(selectionState.devices);
    const connectionIds = Array.from(selectionState.connections);
    const deviceCount = deviceIds.length;
    const connectionCount = connectionIds.length;
    
    // Capture state before deletion for undo
    const deletedDevices = deviceIds.map(id => {
        const device = instanceModel.getById(id);
        return device ? deepClone(device) : null;
    }).filter(Boolean);
    
    // Capture ALL connections involving these devices (not just auto-selected)
    const deletedConnections = connectionModel.filter(c => 
        deviceIds.includes(c.sourceDeviceId) || deviceIds.includes(c.targetDeviceId)
    ).map(c => deepClone(c));
    
    // Create standardized restore functions via UndoManager
    const { restoreAll, removeAll } = createMultiDeviceRestoreFunctions(deletedDevices, deletedConnections);
    
    // Delete all auto-selected connections first
    connectionIds.forEach(connId => {
        connectionModel.remove(connId);
    });
    
    // Delete all selected devices and their connections
    deviceIds.forEach(deviceId => {
        connectionModel.removeByDeviceId(deviceId);
        instanceModel.remove(deviceId);
        document.getElementById(deviceId)?.remove();
    });
    
    // Clear selection
    clearDeviceSelection();
    updateSelectionButtons();
    scheduleAutosave();
    
    // Emit events for each deleted device
    // CableRenderer subscribes → renderAllCables + updateConnectedPorts
    // LibraryController subscribes → renderLibrary
    // Note: Multiple events are batched by browser before repaint
    deviceIds.forEach(deviceId => {
        eventBus.emit(EVENTS.DEVICE_DELETED, { deviceId });
    });
    
    // Record action for undo/redo using UndoManager pattern
    if (!skipHistory && deletedDevices.length > 0) {
        const description = deviceCount === 1 
            ? `Delete device` 
            : `Delete ${deviceCount} devices`;
        
        recordAction(
            'delete-devices',
            description,
            restoreAll,  // Undo: restore all devices and connections
            removeAll    // Redo: delete all again
        );
    }
    
    // Show feedback
    const parts = [];
    if (deviceCount > 0) parts.push(`${deviceCount} device${deviceCount > 1 ? 's' : ''}`);
    if (connectionCount > 0) parts.push(`${connectionCount} cable${connectionCount > 1 ? 's' : ''}`);
    showToast(`Deleted ${parts.join(' and ')}`);
}

/**
 * Delete a single device and its connections
 * @param {string} deviceId - Device ID to delete
 * @param {boolean} skipHistory - If true, don't record to undo history
 */
export function deleteDevice(deviceId, skipHistory = false) {
    if (!deps) {
        console.warn('DeletionController: not initialized');
        return;
    }
    
    const {
        instanceModel,
        connectionModel,
        definitionModel,
        selectionState,
        updateSelectionVisuals,
        updateSelectionButtons,
        scheduleAutosave
    } = deps;
    
    // Capture state before deletion
    const device = instanceModel.getById(deviceId);
    if (!device) return;
    
    const deviceCopy = deepClone(device);
    const template = definitionModel.getForDevice(device);
    const deviceName = template ? template.name : 'device';
    
    // Capture connections involving this device
    const deletedConnections = connectionModel.getByDeviceId(deviceId)
        .map(c => deepClone(c));
    
    // Create standardized restore functions via UndoManager
    const { restoreDevice, removeDevice } = createDeviceRestoreFunctions(deviceCopy, deletedConnections);
    
    // Execute deletion
    connectionModel.removeByDeviceId(deviceId);
    instanceModel.remove(deviceId);
    document.getElementById(deviceId)?.remove();
    selectionState.devices.delete(deviceId);
    updateSelectionVisuals();
    updateSelectionButtons();
    scheduleAutosave();
    
    // Emit device deleted event
    // - CableRenderer subscribes → renderAllCables + updateConnectedPorts
    // - LibraryController subscribes → renderLibrary
    eventBus.emit(EVENTS.DEVICE_DELETED, { deviceId });
    
    // Record action for undo/redo using UndoManager pattern
    if (!skipHistory) {
        recordAction(
            'delete-device',
            `Delete ${deviceName}`,
            restoreDevice,  // Undo: restore device and connections
            removeDevice    // Redo: delete again
        );
    }
    
    showToast('Device deleted');
}

/**
 * Delete a single connection
 * @param {string} connectionId - Connection ID to delete
 * @param {boolean} skipHistory - If true, don't record to undo history
 */
export function deleteConnection(connectionId, skipHistory = false) {
    if (!deps) {
        console.warn('DeletionController: not initialized');
        return;
    }
    
    const {
        connectionModel,
        state,
        updateSelectionButtons,
        scheduleAutosave
    } = deps;
    
    // Capture connection before deletion
    const connection = connectionModel.getById(connectionId);
    if (!connection) return;
    
    const connectionCopy = deepClone(connection);
    
    // Create standardized restore functions via UndoManager
    const { undoConnection, redoConnection } = createConnectionRestoreFunctions(connectionCopy);
    
    // Execute deletion
    connectionModel.remove(connectionId);
    state.selectedConnectionId = null;
    updateSelectionButtons();
    scheduleAutosave();
    
    // Emit connection deleted event (CableRenderer subscribes → renderAllCables + updateConnectedPorts)
    eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId });
    
    // Record action for undo/redo using UndoManager pattern
    if (!skipHistory) {
        recordAction(
            'delete-connection',
            'Delete connection',
            undoConnection,  // Undo: restore connection
            redoConnection   // Redo: delete again
        );
    }
    
    showToast('Connection deleted');
}

/**
 * Delete cables for selected devices
 * - Single device: delete internal connections only
 * - Multiple devices: delete connections between selected devices
 */
export function deleteSelectedCables() {
    if (!deps) {
        console.warn('DeletionController: not initialized');
        return;
    }
    
    const {
        connectionModel,
        selectionState,
        state,
        hasDeviceSelection,
        updateSelectionButtons
    } = deps;
    
    if (!hasDeviceSelection()) {
        // If no device selected but a connection is selected, delete that
        if (state.selectedConnectionId) {
            deleteConnection(state.selectedConnectionId);
        } else {
            showToast('Select device(s) first');
        }
        return;
    }
    
    const deviceIds = Array.from(selectionState.devices);
    let connections = [];
    
    if (deviceIds.length === 1) {
        // Single device: delete INTERNAL connections only (within the device itself)
        const deviceId = deviceIds[0];
        connections = connectionModel.getAll().filter(c =>
            c.sourceDeviceId === deviceId && c.targetDeviceId === deviceId
        );
    } else {
        // Multiple devices: delete connections BETWEEN selected devices (excluding internal routing)
        connections = connectionModel.getAll().filter(c =>
            deviceIds.includes(c.sourceDeviceId) && 
            deviceIds.includes(c.targetDeviceId) &&
            c.sourceDeviceId !== c.targetDeviceId
        );
    }
    
    if (connections.length === 0) {
        const msg = deviceIds.length === 1 
            ? 'No internal routing to clear'
            : 'No connections between selected devices';
        showToast(msg);
        return;
    }
    
    // Make deep copies for undo
    const connectionCopies = connections.map(c => deepClone(c));
    
    // Create standardized restore functions via UndoManager
    const { restoreConnections, removeConnections } = createBatchConnectionRestoreFunctions(connectionCopies);
    
    // Delete all connections
    connections.forEach(c => connectionModel.remove(c.id));
    state.selectedConnectionId = null;
    updateSelectionButtons();
    
    // Emit events for each deleted connection
    // CableRenderer subscribes → renderAllCables + updateConnectedPorts
    connections.forEach(c => {
        eventBus.emit(EVENTS.CONNECTION_DELETED, { connectionId: c.id });
    });
    
    // Build description for undo/redo
    const description = deviceIds.length === 1
        ? `Clear internal routing`
        : `Clear routing between ${deviceIds.length} devices`;
    
    // Record action for undo/redo using UndoManager pattern
    recordAction(
        'clear-routing',
        description,
        restoreConnections,  // Undo: restore all connections
        removeConnections    // Redo: delete all again
    );
    
    showToast(`Cleared ${connections.length} connection${connections.length > 1 ? 's' : ''}`);
}

/**
 * Clear routing for selected devices (alias for deleteSelectedCables)
 */
export function clearRouting() {
    deleteSelectedCables();
}
